/* ⚛ REACT */
import { useEffect, useRef, useState } from "react";

/* 📦 LIBS */
import { useTranslation } from "react-i18next";

/* 🧩 COMPONENTS */
import Graph from "../Components/Graph";
import { Verticalbar, HorizontalBar, Label, Input, Button, CustomLoading, Breadcrumb } from "../../../Components";

/* 🔗 SERVICE */
import { MaintenanceServices } from "../../../Services/maintenanceServices";

/* 🎨 STYLES */
import { Container, SubTitle, TimeSpend, ScaleContainer, HourText, ScrollContainer, HeaderContainer, ScrollColumnContainer,
    ContainerStats, TypeContainer, TypeText, Line, TypeValue, ReportGraph, ReportBox, ReportsContainer } from "./styles";

/* 🔧 UTILS */
import { isValidField } from "../../../util/validate";

const MaintenanceReports = () => {
    const { t } = useTranslation();

    document.title = t("maintenance.sub-items.reports.document-title");

    const navigationFlow =  [
        { text: 'maintenance', page: null },
        { text: 'reports', page: null }
    ]
    
    const Services = useRef(new MaintenanceServices());
    
    const datafim = useRef(new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString().slice(0, 19));
    const datainicio = useRef(new Date((new Date().setMonth(new Date().getMonth() - 6)) - 3 * 60 * 60 * 1000).toISOString().slice(0, 19));
    
    const [maintenanceReports, setMaintenanceReports] = useState([]);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const GetMaintenanceReports = async() => {
            setLoading(true);
            const response = await Services.current.GetMaintenanceReports(new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString().slice(0, 19), 
                new Date((new Date().setMonth(new Date().getMonth() - 6)) - 3 * 60 * 60 * 1000).toISOString().slice(0, 19));
            setMaintenanceReports(response);
            setLoading(false);
        }
        
        GetMaintenanceReports();
    }, []);	

    // Método formatar o horário
    const getHorario = (horario) => {
        let hours = parseInt(horario / 3600);
        let minutes = parseInt((horario % 3600) / 60);
        if(hours > 0){
            horario = hours + "h" + minutes + "m";
        }else{
            horario = minutes + "m";
        }
        return horario;
    }

    const GetMaintenanceReports = async(dataFinal, dataInicial) => {
        setLoading(true);
        const response = await Services.current.GetMaintenanceReports(dataFinal, dataInicial);
        setMaintenanceReports(response);
        setLoading(false);
    }

    return(
        <Container>
            <Breadcrumb flow={navigationFlow}/>
            <HeaderContainer>
                <div>
                    <Label text={t("inputs.initial-date.label")} aling="flex-start"/>
                    <Input 
                        aling="flex-start"
                        id='datainicial' 
                        name='datainicial' 
                        type="datetime-local" 
                        placeholder={t("inputs.initial-date.placeholder")}
                        value={datainicio.current}
                        max={new Date().toISOString().slice(0, 16)}
                        invalidMessage={t("inputs.initial-date.invalid-message")}
                        validationFunction={(text) => isValidField(text)}
                        onchange={(text) => datainicio.current = text}> 
                    </Input>
                </div>
                <div>
                    <Label text={t("inputs.final-date.label")} aling="flex-start"/>
                    <Input 
                        aling="flex-start"
                        id='datafim' 
                        name='datafim' 
                        type="datetime-local" 
                        placeholder={t("inputs.final-date.placeholder")}
                        value={datafim.current}
                        max={new Date().toISOString().slice(0, 16)}
                        invalidMessage={t("inputs.final-date.invalid-message")}
                        validationFunction={(text) => isValidField(text)}
                        onchange={(text) => datafim.current = text}> 
                    </Input>
                </div>
                <Button text={t("buttons.apply")} width={'fit-content'} color={'green'} onClick={() => {GetMaintenanceReports(datafim.current, datainicio.current)}}/>
            </HeaderContainer>
            
            {loading ? <CustomLoading width={'150px'} height={'150px'}/>
                :
                <>
                    <ReportsContainer>
                        {maintenanceReports.length > 0 &&
                            <ReportGraph>
                                <SubTitle>{t("maintenance.sub-items.reports.percentage-maintenance-types.title")}</SubTitle>
                                <ContainerStats>
                                    <Graph data={maintenanceReports[0]}></Graph>
                                    <TypeContainer>
                                        <TypeText>
                                            {t("maintenance.sub-items.reports.percentage-maintenance-types.types.preventive")} : <TypeValue> {maintenanceReports[0][1].value}% </TypeValue>
                                        </TypeText>
                                        <Line/>
                                        <TypeText>
                                            {t("maintenance.sub-items.reports.percentage-maintenance-types.types.corrective")} : <TypeValue> {maintenanceReports[0][0].value}% </TypeValue>
                                        </TypeText>
                                    </TypeContainer>
                                </ContainerStats>
                            </ReportGraph>
                        }

                        {maintenanceReports.length > 0 && Object.values(maintenanceReports[1][2]).length >= 1 &&
                            <ReportBox>
                                <SubTitle>{t("maintenance.sub-items.reports.time-spent-maintenance")}</SubTitle>
                            
                                <TimeSpend>
                                    <ScaleContainer>
                                        <HourText> {getHorario(maintenanceReports[1][1])} </HourText>
                                        <HourText> {getHorario((maintenanceReports[1][1]/2))} </HourText>
                                        <HourText> {getHorario(maintenanceReports[1][0])} </HourText>
                                    </ScaleContainer>

                                    <ScrollContainer>
                                        {Object.entries(maintenanceReports[1][2]).map(value =>
                                            <Verticalbar key={value[0]} height={(value[1]/maintenanceReports[1][1])*100+'%'} name={value[0] ? value[0].slice(0, 10):'SN'}></Verticalbar>
                                        )}
                                    </ScrollContainer>
                                </TimeSpend>
                            </ReportBox> 
                        } 
                    </ReportsContainer>

                    <ReportsContainer>
                        {maintenanceReports.length > 0 && Object.values(maintenanceReports[2][0]).length >= 1 &&
                            <ScrollColumnContainer>
                                <SubTitle> {t("maintenance.sub-items.reports.equipment-frequent-issues")} </SubTitle>
                                {Object.values(maintenanceReports[2][0]).map(equip =>
                                    <HorizontalBar key={equip.id_equipamento}  width={(equip.count/maintenanceReports[2][1])*100+'%'} name={equip.equipamento.nome ? equip.equipamento.nome.slice(0, 10) : equip.equipamento.id} soma={equip.count}></HorizontalBar>
                                )}
                            </ScrollColumnContainer>
                        }
                    </ReportsContainer>
                </>
            }
        </Container>
    )
}

export default MaintenanceReports;